import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import type {PDFPageProxy} from 'pdfjs-dist';
import type {PdfCanvasProps} from './pdf-canvas.types';
import {PDF_CANVAS_MAX_PIXEL_RATIO} from './pdf-canvas.types';
import styles from './pdf-canvas.module.scss';

function appendPageCanvas(container: HTMLDivElement, page: PDFPageProxy, pixelRatio: number): Promise<void> | null {
  const viewport = page.getViewport({scale: pixelRatio});
  const canvas = window.document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (context === null) {
    return null;
  }

  canvas.className = styles.page;
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  canvas.style.width = `${viewport.width / pixelRatio}px`;
  container.appendChild(canvas);

  return page.render({canvas, canvasContext: context, viewport}).promise;
}

export function PdfCanvas({url, className = ''}: PdfCanvasProps): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (container === null || url === '') {
      return;
    }

    let isCancelled = false;
    let destroyLoadingTask: (() => void) | null = null;

    const renderPages = async (): Promise<void> => {
      const pdfjsLib = await import('pdfjs-dist');

      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

      const loadingTask = pdfjsLib.getDocument({url});
      destroyLoadingTask = (): void => {
        void loadingTask.destroy();
      };

      const document = await loadingTask.promise;
      const pageNumbers = Array.from(Array(document.numPages).keys()).map((index) => index + 1);
      const pages = await Promise.all(pageNumbers.map(async (pageNumber) => document.getPage(pageNumber)));

      if (isCancelled) {
        return;
      }

      container.replaceChildren();

      const pixelRatio = Math.min(window.devicePixelRatio, PDF_CANVAS_MAX_PIXEL_RATIO);
      const renderTasks = pages.map((page) => appendPageCanvas(container, page, pixelRatio)).filter((task): task is Promise<void> => task !== null);

      await Promise.all(renderTasks);
    };

    void renderPages();

    return (): void => {
      isCancelled = true;
      destroyLoadingTask?.();
    };
  }, [url]);

  return <div ref={containerRef} className={`${styles.root} ${className}`} />;
}
